<script lang="ts">
  import { pick } from "lodash";
  import { form, bindClass } from "@shared/lib/svelte-forms";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
  import type { RetailerType } from "@shared/types/Retailer";

  export let retailer: RetailerType;

  const { daysOfWeek, retailerLicenseTypes, orderFrequency } = window["C"];

  let errors = {};
  let secondaryOrderDay = { hide: !retailer.order_day_2 };
  let submitting: boolean = false;

  const businessDetailsForm = form(
    () => ({
      liquor_license: { value: retailer.liquor_license, validators: ["required"] },
      license_type: { value: retailer.license_type, validators: ["required"] },
      order_day: { value: retailer.order_day, validators: ["required"] },
      receipt_day: { value: retailer.receipt_day, validators: ["required"] },
      order_frequency: { value: retailer.order_frequency },
      order_day_2: { value: retailer.order_day_2 },
      receipt_day_2: { value: retailer.receipt_day_2 },
      liquor_license_ack: {
        value: retailer.liquor_license_ack,
        validators: [isValidAck],
      },
    }),
    {
      initCheck: false,
    },
  );

  const submit = async (): Promise<void> => {
    submitting = true;

    const data = {
      retailer: pick(retailer, [
        "license_terms_ack_by_user_id",
        "license_type",
        "liquor_license_ack",
        "liquor_license",
        "order_day_2",
        "order_day",
        "order_frequency",
        "receipt_day_2",
        "receipt_day",
      ]),
    };

    if (!retailer.license_terms_ack_by_user_id && retailer.liquor_license_ack) {
      data.retailer.license_terms_ack_by_user_id = $CurrentUserStore["id"];
    }

    await HttpPromise({
      method: "PUT",
      url: "/api/retailer/retailers/" + retailer.id,
      data,
    })
      .then(_res => {
        errors = {};
        businessDetailsForm.reset();
        Flash.now({ type: "success", message: "We've updated your information!" });
      })
      .catch(err => {
        errors = err;
        Flash.now({ type: "error", message: "There was an error updating your information." });
      })
      .finally(() => (submitting = false));
  };

  const formKeyUp = (event: KeyboardEvent): void => {
    if (event.keyCode === 13 && !doNotAllowFormSubmit) submit();
  };

  const toggleSecondaryOrderDayFields = (_e: Event): void => {
    secondaryOrderDay.hide = !secondaryOrderDay.hide;
  };

  function isValidAck(checked: boolean): { name: string; valid: boolean } {
    return {
      name: "required",
      valid: retailer.non_alcoholic || !!retailer.liquor_license ? !!checked : true,
    };
  }

  $: doNotAllowFormSubmit =
    submitting || !$businessDetailsForm.valid || !$businessDetailsForm.dirty;
  $: errors;
</script>

<div class="margin-bottom--small">
  <div class="fizz-flex-row">
    <h3 class="fizz-inset-squish-16 fizz-heading-3 fizz-text-strong fizz-stack-16">
      Location Information for <span class="notranslate">{retailer.name}</span>
    </h3>
  </div>
  <form
    class="fizz-form"
    style="--max-width: 615px; max-width: var(--max-width);"
    on:keyup|preventDefault={formKeyUp}
  >
    {#if !retailer.non_alcoholic}
      <div class="fizz-flex-row-responsive fizz-inset-squish-16">
        <div class="fizz-flex-item">
          <label class="fizz-label fizz-stack-4" for="liquor_license">
            Liquor License Number*
          </label>
          <input
            class="fizz-input-text fizz-full-width"
            disabled={submitting || $CurrentUserStore.role !== "retailer_admin"}
            id="liquor_license"
            name="liquor_license"
            type="text"
            bind:value={retailer.liquor_license}
            use:bindClass={{ form: businessDetailsForm }}
          />
        </div>
        <div class="fizz-flex-item">
          <label class="fizz-label fizz-stack-4" for="retailer_license_type"> Premise Type* </label>
          <select
            class="fizz-select full-width"
            id="retailer_license_type"
            name="license_type"
            required
            bind:value={retailer.license_type}
            use:bindClass={{ form: businessDetailsForm }}
          >
            {#each retailerLicenseTypes as licenseType}
              <option value={licenseType.value}>{licenseType.label}</option>
            {/each}
          </select>
        </div>
      </div>
      <div class="fizz-flex-row-responsive fizz-inset-squish-16">
        <label
          class="fizz-flex-item fizz-flex-row fizz-stack-12 fizz-text-small"
          for="liquor_license_ack"
        >
          <div>
            <input
              class="fizz-checkbox"
              disabled={!retailer.liquor_license || retailer.non_alcoholic}
              id="liquor_license_ack"
              name="liquor_license_ack"
              type="checkbox"
              use:bindClass={{ form: businessDetailsForm }}
              bind:checked={retailer.liquor_license_ack}
            />
          </div>
          <span class:fizz-subdued={!retailer.liquor_license || retailer.non_alcoholic}>
            I certify that the above-referenced liquor license is currently valid and duly issued by
            the appropriate governmental agency.
          </span>
        </label>
        <div class="fizz-flex-item" />
      </div>
    {/if}
    <div class="fizz-flex-row-responsive fizz-inset-squish-16">
      <div class="fizz-flex-item">
        <label class="fizz-label fizz-stack-4" for="order_day_select">Typical Order Day*</label>
        <select
          class="fizz-select full-width"
          id="order_day_select"
          name="order_day"
          required
          bind:value={retailer.order_day}
          use:bindClass={{ form: businessDetailsForm }}
        >
          <option value="">Choose a day</option>
          {#each daysOfWeek as value}
            <option {value}>{value}</option>
          {/each}
        </select>
      </div>
      <div class="fizz-flex-item">
        <label class="fizz-label fizz-stack-4" for="receipt_day_select">
          Typical Receipt Day*
        </label>
        <select
          class="fizz-select full-width"
          id="receipt_day_select"
          name="receipt_day"
          required
          bind:value={retailer.receipt_day}
          use:bindClass={{ form: businessDetailsForm }}
        >
          <option value="">Choose a day</option>
          {#each daysOfWeek as value}
            <option {value}>{value}</option>
          {/each}
        </select>
      </div>
    </div>
    <div class="fizz-flex-row-responsive fizz-inset-squish-16">
      <div class="fizz-flex-item">
        <label class="fizz-label fizz-stack-4" for="select_order_frequency">
          Order Frequency
        </label>
        <select
          class="fizz-select fizz-full-width"
          id="select_order_frequency"
          name="order_frequency"
          bind:value={retailer.order_frequency}
          use:bindClass={{ form: businessDetailsForm }}
        >
          {#each orderFrequency as { key, value }}
            <option value={key}>{value}</option>
          {/each}
        </select>
      </div>
      <div class="fizz-flex-item" />
    </div>
    {#if retailer.order_day}
      <div class="fizz-flex-row-responsive fizz-inset-squish-16">
        {#if secondaryOrderDay.hide}
          <span
            class="fizz-text-brand underline-hover cursor-pointer"
            id="displaySecondaryOrderDays"
            on:click={toggleSecondaryOrderDayFields}
            on:keyup={toggleSecondaryOrderDayFields}
          >
            Do you order multiple times a week? Add an additional order day.
          </span>
        {/if}
        {#if !secondaryOrderDay.hide}
          <div class="fizz-flex-item">
            <label class="fizz-label fizz-stack-4" for="order_day_select_2">
              Secondary Order Day
            </label>
            <select
              class="fizz-select fizz-full-width"
              id="order_day_select_2"
              name="order_day_2"
              bind:value={retailer.order_day_2}
              use:bindClass={{ form: businessDetailsForm }}
            >
              {#each daysOfWeek as value}
                <option {value}>{value}</option>
              {/each}
            </select>
          </div>
          <div class="fizz-flex-item">
            <label class="fizz-label fizz-stack-4" for="receipt_day_select_2">
              Secondary Receipt Day
            </label>
            <select
              class="fizz-select fizz-full-width"
              id="receipt_day_select_2"
              name="receipt_day_2"
              bind:value={retailer.receipt_day_2}
              use:bindClass={{ form: businessDetailsForm }}
            >
              {#each daysOfWeek as value}
                <option {value}>{value}</option>
              {/each}
            </select>
          </div>
        {/if}
      </div>
    {/if}
    <div class="fizz-flex-row-responsive fizz-inset-squish-16">
      <button
        class="fizz-button-primary"
        disabled={doNotAllowFormSubmit}
        on:click|preventDefault={submit}
      >
        Update
      </button>
    </div>
  </form>
</div>
